import React, { useCallback, useContext, useRef } from 'react';
import {
  oneOf,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType
} from 'prop-types';
import { useDataModel, QueryProvider } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import {
  RecommendationsProductPodCarousel
} from '../core/RecommendationsProductPodCarousel';
import { getCustomerData, getSkippers } from '../utils/helpers';
import { reorderDataModel } from '../dataModel';
import { FallbackCard } from '../core/FallbackCard';

const ReorderRecommendations = (props) => {
  const {
    brandTitleMaxLine,
    customEventObject,
    customerID,
    customerIsLoggedIn,
    customerSegment,
    customerType,
    excludeItems,
    filter,
    hideTitle,
    maxProducts,
    requestKey,
    showLoading,
    slidesPer,
    showFallback,
    hideATC,
    keepAddToCart,
    noATCFulfillment,
    entryId
  } = props;

  const { isLocalized, storeId, storeZip: zipCode } = useStore();
  const { channel } = useContext(ExperienceContext) || {};
  const innerQueryPrerequisites = useRef({ storeId, zipCode });
  const isMounted = useCallback(() => {
    return innerQueryPrerequisites.current.mounted;
  }, [innerQueryPrerequisites.current.mounted]);

  const {
    customerType: cType, isB2BCustomer, isLoggedIn, mcvisID, segment, svocID
  } = getCustomerData();

  const custIsLoggedIn = customerIsLoggedIn || isLoggedIn;
  const custID = customerID || custIsLoggedIn ? svocID : mcvisID;
  const custSegment = customerSegment || segment;
  const custType = customerType || cType;

  const b2bVariables = { cSeg: custSegment };
  const apiName = 'reorder';

  const udsQueryOptions = {
    variables: {
      apiName,
      appId: channel === 'mobile' ? 'mobileweb' : 'desktop',
      cType: custType,
      exItems: excludeItems,
      dataSource: apiName,
      filter,
      key: requestKey,
      maxProducts,
      model: isB2BCustomer ? 'model2' : 'model1',
      showPrice: true,
      storeId,
      tableName: 'reorder', // B2C non-auth: thda.m or mcvisidd, B2C and B2B auth: svocid
      userId: custID || '',
      zipCode,
      ...getSkippers({ skipSubscribeAndSave: false }),
      ...(isB2BCustomer && { ...b2bVariables }),
    },
    skip: !isLocalized || storeId === '8119',
    ssr: false,
  };

  const { loading, data, error, variables } = useDataModel('uds', udsQueryOptions);
  if (data && isLocalized) {
    innerQueryPrerequisites.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
      mounted: true,
    };
  }

  if (error || (!data?.uds?.products?.length && !loading)) {
    if (showFallback) {
      return (
        <FallbackCard
          imageUrl="https://assets.thdstatic.com/images/v1/subscription.svg"
          header="Never Run Out of Your Favorite"
          subheader="Save 5% on subscription items"
          footerUrl="https://www.homedepot.com/c/subscription_service"
          footer="Explore Subscriptions"
        />
      );
    }

    return null;
  }
  const impressionData = {
    id: entryId || '',
    name: ReorderRecommendations.displayName,
    component: ReorderRecommendations.displayName,
    type: 'product',
    position: ''
  };

  return (
    <ImpressionProvider data={impressionData}>
      <Row>
        <Col id={apiName} className="dynamic-recs uds-reorder" data-type="container">
          <QueryProvider
            cacheKey="user-data-services-reorder"
            mounted={isMounted}
            defaultVariables={{
              storeId: innerQueryPrerequisites.current.storeId,
              zipCode: innerQueryPrerequisites.current.zipCode,
            }}
          >
            <meta data-prop="name" content={apiName} />
            <RecommendationsProductPodCarousel
              customEventObject={customEventObject}
              data={data?.uds}
              hideRating
              hideTitle={hideTitle}
              hideATC={hideATC}
              itemClass="fixed-item"
              loading={loading}
              showLoading={showLoading}
              showSubscribeAndSave
              scheme={apiName}
              storeId={storeId}
              brandTitleMaxLine={brandTitleMaxLine}
              zipCode={zipCode}
              slidesPer={slidesPer}
              keepAddToCart={keepAddToCart}
              noATCFulfillment={noATCFulfillment}
              componentName={ReorderRecommendations.displayName}
              entryId={entryId}
            />
          </QueryProvider>
        </Col>
      </Row>
    </ImpressionProvider>
  );
};

ReorderRecommendations.dataModel = reorderDataModel;

ReorderRecommendations.displayName = 'ReorderRecommendations';

ReorderRecommendations.propTypes = {
  // product pod takes only these prop values for brandTitleMaxLine
  brandTitleMaxLine: oneOf([2, 3, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  // svocid or mcvisid
  customerID: stringType,
  customerIsLoggedIn: stringType,
  customerSegment: stringType,
  customerType: stringType,
  excludeItems: stringType,
  filter: stringType,
  hideTitle: boolType,
  maxProducts: numberType,
  requestKey: stringType.isRequired,
  showLoading: boolType,
  slidesPer: numberType,
  showFallback: boolType,
  hideATC: boolType,
  keepAddToCart: boolType,
  noATCFulfillment: boolType,
  entryId: stringType
};

ReorderRecommendations.defaultProps = {
  brandTitleMaxLine: 2,
  customEventObject: null,
  customerID: null,
  customerIsLoggedIn: null,
  customerSegment: null,
  customerType: null,
  excludeItems: '',
  filter: '',
  hideTitle: false,
  maxProducts: 20,
  showLoading: false,
  slidesPer: null,
  showFallback: false,
  hideATC: false,
  keepAddToCart: null,
  noATCFulfillment: null,
  entryId: null
};

export { ReorderRecommendations };
